import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, ShieldCheck, Eye, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp, MaskedLine, CountUp } from "@/components/Motion";

const DEPOT_IMG = "https://images.unsplash.com/photo-1723134091310-e6c6f5a65573?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHx0cnVjayUyMGRyaXZlciUyMGxvZ2lzdGljcyUyMGRlcG90JTIwcG9ydCUyMHNvdXRoYW1wdG9ufGVufDB8fHx8MTc5MDU5ODM4Mnww&ixlib=rb-4.1.0&q=85";
const FLEET_IMG = "https://images.unsplash.com/photo-1640296278310-ac562e1eb272?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxoZWF2eSUyMGVxdWlwbWVudCUyMG1hY2hpbmVyeSUyMHRyYW5zcG9ydCUyMGxvcnJ5JTIwdHJhaWxlcnxlbnwwfHx8fDE3OTA1OTgzODJ8MA&ixlib=rb-4.1.0&q=85";

const VALUES = [
  { icon: Clock, title: "Punctuality", copy: "A collection window is a promise. Our 99.8% on-time record exists because we plan every route like it matters — because it does." },
  { icon: ShieldCheck, title: "Vehicle integrity", copy: "Photographed, condition-checked and secured by trained crews. Your vehicle arrives in exactly the state it left." },
  { icon: Eye, title: "Complete transparency", copy: "Fixed quotes, protected payments and honest updates. You'll always know where your vehicle — and your money — stands." },
];

const REGIONS = [
  { name: "Yorkshire HQ", cities: "Tadcaster · York · Leeds · Harrogate" },
  { name: "London & South East", cities: "London · Reading · Brighton · Guildford" },
  { name: "Midlands", cities: "Birmingham · Coventry · Leicester · Nottingham" },
  { name: "The North", cities: "Manchester · Leeds · Liverpool · Newcastle" },
  { name: "Scotland & Wales", cities: "Glasgow · Edinburgh · Cardiff · Swansea" },
  { name: "Ports & Docks", cities: "Hull · Felixstowe · Liverpool · Dover" },
];

const About = () => (
  <div data-testid="about-page">
    <Seo
      title="About Us | Solaris Transport LTD, Tadcaster"
      description="Solaris Transport LTD is a North Yorkshire vehicle and equipment transport company with 20+ years' experience moving cars, plant and machinery across the UK."
      path="/about"
    />

    {/* Hero */}
    <section className="relative overflow-hidden border-b border-slate-200 bg-white" data-testid="about-hero">
      <div className="pointer-events-none absolute -right-24 top-10 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl" />
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xs font-semibold uppercase tracking-widest text-blue-700"
            data-testid="about-eyebrow"
          >
            About Solaris Transport
          </motion.p>
          <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="about-headline">
            <MaskedLine delay={0.1}>The Tadcaster team</MaskedLine>
            <MaskedLine delay={0.22}>behind <span className="text-blue-700">1,650+</span> safe</MaskedLine>
            <MaskedLine delay={0.34}>deliveries.</MaskedLine>
          </h1>
        </div>
      </div>
    </section>

    {/* Story */}
    <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28" data-testid="about-story-section">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <FadeUp>
          <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-xl shadow-blue-900/5">
            <img src={DEPOT_IMG} alt="Solaris Transport logistics depot near Tadcaster, North Yorkshire" className="h-96 w-full object-cover" data-testid="about-story-image" />
          </div>
        </FadeUp>
        <FadeUp delay={0.15}>
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-700">Our story</p>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Two decades on Britain's roads
          </h2>
          <div className="mt-5 space-y-4 text-base leading-relaxed text-slate-600">
            <p>
              Solaris Transport LTD began over twenty years ago with a single
              transporter and a simple rule: treat every vehicle like it's your
              own. Today, from our base at Wighill Park near Tadcaster, our
              lorries cover the whole of the UK.
            </p>
            <p>
              Sitting just off the A1(M), the whole country is within easy
              reach. Whether it's a family car heading to a new home in
              Bristol, a digger bound for a site in Glasgow, or a classic going
              to auction in London, the same care goes into every single
              movement.
            </p>
            <p>
              And because trust should be built in, not asked for, every booking
              comes with independent, FCA-regulated payment protection — your
              money is only released when you confirm safe delivery.
            </p>
          </div>
          <div className="mt-8 flex gap-10" data-testid="about-mini-stats">
            <div>
              <p className="font-display text-3xl font-extrabold text-blue-700"><CountUp end={20} suffix="+" /></p>
              <p className="text-xs font-semibold text-slate-500">years' experience</p>
            </div>
            <div>
              <p className="font-display text-3xl font-extrabold text-blue-700"><CountUp end={1650} suffix="+" /></p>
              <p className="text-xs font-semibold text-slate-500">vehicles delivered</p>
            </div>
            <div>
              <p className="font-display text-3xl font-extrabold text-blue-700"><CountUp end={1580} suffix="+" /></p>
              <p className="text-xs font-semibold text-slate-500">satisfied clients</p>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>

    {/* Values */}
    <section className="border-y border-slate-200 bg-slate-50" data-testid="about-values-section">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <FadeUp>
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-700">What we stand for</p>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Three values, every single job
          </h2>
        </FadeUp>
        <div className="mt-10 grid gap-5 md:grid-cols-3" data-testid="about-values-grid">
          {VALUES.map((v, i) => (
            <FadeUp key={v.title} delay={i * 0.1}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-7" data-testid={`about-value-${i}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-700">
                  <v.icon className="h-6 w-6 text-white" />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold text-slate-900">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.copy}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    {/* Coverage */}
    <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28" data-testid="about-coverage-section">
      <FadeUp>
        <p className="text-xs font-semibold uppercase tracking-widest text-blue-700">UK-wide coverage</p>
        <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
          Based in Tadcaster. At home everywhere.
        </h2>
      </FadeUp>
      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="about-coverage-grid">
        {REGIONS.map((r, i) => (
          <FadeUp key={r.name} delay={(i % 3) * 0.08}>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 transition-colors duration-300 hover:border-blue-300" data-testid={`coverage-card-${i}`}>
              <div className="flex items-center gap-2.5">
                <MapPin className="h-4 w-4 text-blue-700" />
                <h3 className="font-display text-base font-semibold text-slate-900">{r.name}</h3>
              </div>
              <p className="mt-3 font-mono text-xs leading-relaxed tracking-wide text-slate-500">{r.cities}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>

    {/* Fleet */}
    <section className="border-t border-slate-200 bg-white" data-testid="about-fleet-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-20 lg:grid-cols-2 lg:px-8 lg:py-28">
        <FadeUp>
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-700">Fleet &amp; equipment</p>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            The right lorry for every load
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600">
            Our fleet is specified for the work we actually do — no job is
            squeezed onto the wrong trailer.
          </p>
          <ul className="mt-6 space-y-3" data-testid="about-fleet-list">
            {[
              "Multi-car transporters for single vehicles and full loads",
              "Low loaders for plant, machinery and heavy equipment",
              "Enclosed covered trailers for prestige and classic vehicles",
              "4x4 and trailer units for caravans, boats and leisure craft",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-slate-700">
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-700" />
                {item}
              </li>
            ))}
          </ul>
          <Link to="/contact" data-testid="about-fleet-quote-button" className="group mt-8 inline-flex items-center gap-2 rounded-full bg-blue-700 px-7 py-3.5 text-base font-bold text-white transition-colors duration-200 hover:bg-blue-800">
            Discuss your load <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
        <FadeUp delay={0.15}>
          <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-xl shadow-blue-900/5">
            <img src={FLEET_IMG} alt="Solaris Transport logistics vehicle fleet ready for UK delivery" className="h-96 w-full object-cover" data-testid="about-fleet-image" />
          </div>
        </FadeUp>
      </div>
    </section>
  </div>
);

export default About;
